import * as React from 'react';
import { FieldHookConfig } from 'formik';
import { useDropzone } from 'react-dropzone';
import { styled } from '@material-ui/core';
import { TextFieldProps } from 'app/components/TextField';
import { ImprotData } from '../Data';
import { ImageList, Image, FileSize } from './ImageList';
import { Button } from 'app/components/BasicButtons/Button';
import { useTranslation } from 'react-i18next';
import { translations } from 'locales/translations';
import { Box } from 'app/components/basic/Wrappers/Box';
import { IconButton } from 'app/components/BasicButtons/IconButton';
import { Icon } from 'app/components/BasicIcons/FontAwesome';
import { ExportImportActions } from '../Actions';
import { ScreensId } from 'enums/ConfigurableTypes';
import fileDownload from 'js-file-download';
import { AxiosError } from 'axios';
import { useDispatch, useSelector } from 'react-redux';
import { useAppSettingsSlice } from 'app/slice';
import { tryParseInt } from 'utils/string-utils';
import { selectglobalSettings } from 'app/slice/selectors';
import { AllowedSettings } from 'utils/globalSettings';

export interface SelectImageFilesProps {
  accept: any;
  filesPlaceholder: React.ReactNode;
}

/**
 * Multiline text field with and option to update value by dropping a file
 * @param param0
 * @returns
 */
const getFileSize = (size): FileSize => {
  var string;
  if (size >= 1024 * 1024 * 1024 * 1024) {
    size = size / (1024 * 1024 * 1024 * 1024);
    string = 'TiB';
  } else if (size >= 1024 * 1024 * 1024) {
    size = size / (1024 * 1024 * 1024);
    string = 'GiB';
  } else if (size >= 1024 * 1024) {
    size = size / (1024 * 1024);
    string = 'MiB';
  } else if (size >= 1024) {
    size = size / 1024;
    string = 'KiB';
  } else {
    string = 'b';
  }
  return { size: Math.round(size), name: string };
};

export const SelectImageFiles = ({
  label,
  onBlur,
  value,
  name,
  error,
  helperText,
  accept,
  filesPlaceholder,
  ...props
}: FieldHookConfig<string> & TextFieldProps & SelectImageFilesProps) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const { actions } = useAppSettingsSlice();
  const settings = useSelector(selectglobalSettings);
  const [importing, setImporting] = React.useState<boolean>(false);
  const [paths, setPaths] = React.useState<Image[] | undefined>();
  const MaximumImagesPerOneUpload =
    tryParseInt(
      settings?.GetString(AllowedSettings.MaximumImagesPerOneUpload),
    ) ?? 10;
  const MaxImageSize =
    (tryParseInt(
      settings?.GetString(AllowedSettings.MaxLoadedAssetImageMbSize),
    ) ?? 1) *
    1024 *
    1024;
  const setImages = React.useCallback(() => {
    if (ImprotData.Images !== undefined) {
      setPaths(
        ImprotData.Images.map(file => {
          return {
            name: file.name,
            path: URL.createObjectURL(file),
            size: getFileSize(file.size),
          };
        }),
      );
    } else {
      setPaths(undefined);
    }
  }, []);
  React.useEffect(() => {
    setImages();
  }, [setImages]);
  const onDrop = React.useCallback(
    (acceptedFiles: File[]) => {
      ImprotData.AddImages(acceptedFiles);
      setImages();
    },
    [setImages],
  );
  const removeImage = React.useCallback(
    (name: string) => {
      ImprotData.RemoveImage(name);
      setImages();
    },
    [setImages],
  );

  const { getRootProps, getInputProps } = useDropzone({
    onDrop,
    maxFiles: MaximumImagesPerOneUpload,
    maxSize: MaxImageSize,
    accept: accept,
  });

  const importImages = React.useCallback(async () => {
    if (ImprotData.Images !== undefined) {
      setImporting(true);
      try {
        const res = await ExportImportActions.ImportImages(
          ImprotData.Images,
          ScreensId.MyAssets,
        );
        const cd = res.headers['content-disposition'];
        const filename =
          cd !== undefined
            ? cd.split('filename=')[1]
            : 'ExportForImport_Articles.xlsx';
        fileDownload(res.data, filename);
        ImprotData.ClearImages();
        setPaths(undefined);
        dispatch(
          actions.addNotification({
            variant: 'success',
            message: 'Images loaded successfully !',
          }),
        );
      } catch (error) {
        if ((error as AxiosError).isAxiosError) {
          //var errorCode = (error as AxiosError).response?.status;
          dispatch(
            actions.addNotification({
              variant: 'error',
              message: error as string,
            }),
          );
        }
      }
      setImporting(false);
    }
  }, [actions, dispatch]);

  return (
    <>
      <div style={{ display: 'flex', alignItems: 'center' }}>
        {t(translations.RecommendationsOfLoadImages)}
        <IconButton
          title={t(translations.RecommendationsBodyOfLoadImages)}
          variant="ghost"
          shape="square"
          size="small"
        >
          <Icon icon="info-circle" color="primary" />
        </IconButton>
      </div>
      <div style={{ display: 'flex', alignItems: 'center' }}>
        {t(translations.InformationToLoadImages)}
        <IconButton
          title={t(translations.RecommendationsBodyOfLoadImages)}
          variant="ghost"
          shape="square"
          size="small"
        >
          <Icon icon="info-circle" color="primary" />
        </IconButton>
      </div>
      <StyledDropboxContainer {...getRootProps({ className: 'dropzone' })}>
        <input {...getInputProps()} />
        <p>{filesPlaceholder}</p>
      </StyledDropboxContainer>
      <ImageList data={paths} removeImage={removeImage} />
      <Box style={{ display: 'flex', marginTop: '20px' }}>
        <Button
          processing={importing}
          disabled={paths === undefined || paths.length === 0}
          onClick={importImages}
        >
          {t(translations.LoadImages)}
        </Button>
        <Button
          style={{ marginLeft: '10px' }}
          variant="white"
          disabled={paths === undefined || paths.length === 0}
          onClick={() => {
            ImprotData.ClearImages();
            setPaths(undefined);
          }}
        >
          {t(translations.ClearAll)}
        </Button>
      </Box>
    </>
  );
};
const StyledDropboxContainer = styled('div')(({ theme }) => ({
  flex: 1,
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  padding: '20px',
  borderWidth: '2px',
  borderRadius: '2px',
  borderColor: theme.palette.common.grayBorders,
  borderStyle: 'dashed',
  backgroundColor: theme.palette.background.paper,
  color: '#bdbdbd',
  outline: 'none',
  transition: 'border .24s ease-in-out',
}));
